import { useRef, useState } from 'react';

import { DatasetProvider } from 'pages/map-query/dataset-context';
import { DatasetLifecycle } from 'pages/map-query/dataset-lifecycle';
import { createDatasetAtoms } from 'pages/map-query/atoms';
import { FilterPanelToggle } from 'components/collapsible-filter-panel';
import { FilterRail } from 'pages/map-query/filter-modal/filter-rail';
import {
  PREVIEW_HOST_ID,
  ResultsSection,
} from 'pages/map-query/results-section/results-section';
import type { MapQueryExport } from 'pages/map-query/use-map-query-export';
import { useMediaQuery } from 'lib/hooks/useMediaQuery';
import { useSpaceBelow } from 'lib/hooks/use-space-below';
import type { EntityResponse } from 'types/api-responses';
import { entityDocumentsConfig } from './entity-documents-utils';
import style from './entity-documents-tab.module.scss';

// Below this the open rail leaves the seven-column cards too little room for the
// Document column, so the filters start hidden (a click shows them).
const NARROW_QUERY = '(max-width: 1439px)';

interface EntityDocumentsTabProps {
  entity: EntityResponse;
  // Owned by the entity page, so a running export survives a tab switch.
  mapQueryExport: MapQueryExport;
}

/**
 * The entity's documents as Map Query's documents runsheet: the same cards,
 * filters, preview and export, scoped to this entity. Its dataset lives and dies
 * with the tab, so leaving the tab resets it like every other entity tab.
 * @param {EntityDocumentsTabProps} props The tab props.
 * @return {JSX.Element} The tab.
 */
export function EntityDocumentsTab({
  entity,
  mapQueryExport,
}: EntityDocumentsTabProps) {
  // A dataset belongs to one entity: showing another starts a new one.
  return (
    <EntityDocuments
      key={entity.id}
      entity={entity}
      mapQueryExport={mapQueryExport}
    />
  );
}

/**
 * One entity's documents runsheet, its dataset created once for that entity.
 * @param {EntityDocumentsTabProps} props The runsheet props.
 * @return {JSX.Element} The runsheet.
 */
function EntityDocuments({ entity, mapQueryExport }: EntityDocumentsTabProps) {
  const [config] = useState(() => entityDocumentsConfig(entity.id));
  const [atoms] = useState(() => createDatasetAtoms(config));
  const narrow = useMediaQuery(NARROW_QUERY);
  const [filtersOpen, setFiltersOpen] = useState(!narrow);
  const layoutRef = useRef<HTMLDivElement>(null);
  useSpaceBelow(layoutRef);

  return (
    <DatasetProvider config={config} atoms={atoms}>
      <DatasetLifecycle atoms={atoms} />
      {/* One viewport tall below the page's sticky header, so scrolling to the
          end of the page brings the whole tab into view under that header; the
          filters and the runsheet each scroll inside it. */}
      <div ref={layoutRef} className={style.layout}>
        <FilterRail isOpen={filtersOpen} />
        {/* The document preview portals into this host and spans its height,
            so the results toolbar and the preview's Close/Prev/Next sit at its
            top. */}
        <div
          id={PREVIEW_HOST_ID}
          className={style.previewHost}
          role="region"
          aria-label="Documents runsheet"
        >
          <ResultsSection
            mapQueryExport={mapQueryExport}
            toolbarStart={
              <FilterPanelToggle
                isOpen={filtersOpen}
                onToggle={() => setFiltersOpen(!filtersOpen)}
              />
            }
          />
        </div>
      </div>
    </DatasetProvider>
  );
}
